/**
 * A ficha de uma nota fiscal importada: quem vendeu, em que situação ela está,
 * a chave de acesso, os itens e o que a nota ainda deve produzir.
 *
 * Componente COMPARTILHADO entre a listagem (gaveta) e qualquer tela que
 * precise mostrar uma nota. A alternativa — duplicar a lista de itens e a
 * lógica de conciliação — divergiria na primeira mudança de regra; um
 * componente só é o que garante que "conciliar" e "lançar" se comportam igual
 * não importa de onde a pessoa chegou.
 *
 * # A forma da ficha
 *
 * Ela é uma FOLHA DE LEITURA, não um formulário: cabeçalho identificando o
 * documento, um bloco de situação, a chave, os itens em linhas e o total
 * fechando embaixo. O item é uma LINHA — descrição, `2 × 39,90 · CP-001`,
 * valor à direita —, e não uma tabela de seis colunas: quem abre esta gaveta
 * está comparando uma nota com o DANFE na mão, não auditando coluna a coluna.
 * A tabela larga voltaria a fazer sentido se a nota ganhasse imposto por item.
 *
 * # A regra central
 *
 * "Lançar no estoque" fica desabilitado enquanto houver item PENDENTE sem uma
 * decisão EXPLÍCITA — conciliar com um SKU, ou marcar "pular este item". Nunca
 * lança estoque de item não decidido em silêncio. "Pular" é só um estado local
 * desta tela — não chama a API — e existe para o lojista dizer "este eu decido
 * depois" sem travar os outros.
 */
import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'

import { Botao } from '../../componentes/Botao'
import { Icone } from '../../componentes/Icone'
import { Selo } from '../../componentes/Selo'
import { ErroApi } from '../../plataforma/api'
import { formatar, formatarMoeda } from '../../plataforma/dinheiro'
import { mascararCNPJ } from '../../plataforma/mascaras'
import {
  carregarFichaNotaFiscal,
  conciliarItem,
  contarSemDecisao,
  lancarNoEstoque,
  type FichaNota,
  type ItemNota,
  type ResultadoLancamento,
} from '../../plataforma/notasfiscais'
import { BuscaDeSKU } from '../produtos/BuscaDeSKU'

type Decisoes = Partial<Record<number, 'pular'>>

/** Devolve `decisoes` sem a entrada de `numeroItem` — imutável, para `setState`. */
function semADecisao(decisoes: Decisoes, numeroItem: number): Decisoes {
  const resto: Decisoes = {}
  for (const chave of Object.keys(decisoes)) {
    const numero = Number(chave)
    if (numero !== numeroItem) resto[numero] = decisoes[numero]
  }
  return resto
}

const ROTULO = 'text-[11px] font-bold tracking-[0.6px] uppercase text-texto-suave'

export function FichaNotaFiscal({
  chave,
  fichaInicial,
  aoAtualizar,
  aoFechar,
}: {
  chave: string
  /** Quando já se tem a ficha (resposta do POST), evita um GET a mais. */
  fichaInicial?: FichaNota
  aoAtualizar?: (ficha: FichaNota) => void
  /** Quando presente, a ficha desenha o próprio rodapé com "Fechar". */
  aoFechar?: () => void
}) {
  const [ficha, setFicha] = useState<FichaNota | null>(fichaInicial ?? null)
  const [carregando, setCarregando] = useState(!fichaInicial)
  const [erro, setErro] = useState('')
  const [aviso, setAviso] = useState('')
  const [decisoes, setDecisoes] = useState<Decisoes>({})
  const [conciliandoItem, setConciliandoItem] = useState<number | null>(null)
  const [lancando, setLancando] = useState(false)
  const [resultadoLancamento, setResultadoLancamento] = useState<ResultadoLancamento | null>(null)

  useEffect(() => {
    if (fichaInicial) return
    const controle = new AbortController()
    setCarregando(true)
    carregarFichaNotaFiscal(chave, controle.signal)
      .then((dados) => {
        setFicha(dados)
        setErro('')
      })
      .catch((causa: unknown) => {
        if (causa instanceof DOMException && causa.name === 'AbortError') return
        setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível abrir a nota.')
      })
      .finally(() => setCarregando(false))
    return () => controle.abort()
  }, [chave])

  function atualizarFicha(nova: FichaNota) {
    setFicha(nova)
    aoAtualizar?.(nova)
  }

  async function conciliar(item: ItemNota, codigoBarras: string) {
    setErro('')
    setConciliandoItem(item.numero_item)
    try {
      const nova = await conciliarItem(chave, item.numero_item, codigoBarras)
      atualizarFicha(nova)
      setDecisoes((atual) => semADecisao(atual, item.numero_item))
    } catch (causa: unknown) {
      setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível conciliar o item.')
    } finally {
      setConciliandoItem(null)
    }
  }

  async function lancar() {
    setErro('')
    setLancando(true)
    setResultadoLancamento(null)
    try {
      const resultado = await lancarNoEstoque(chave)
      atualizarFicha(resultado.nota)
      setResultadoLancamento(resultado)
    } catch (causa: unknown) {
      setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível lançar a nota no estoque.')
    } finally {
      setLancando(false)
    }
  }

  async function copiarChave() {
    if (!ficha) return
    try {
      await navigator.clipboard.writeText(ficha.chave_acesso)
      setAviso('Chave de acesso copiada.')
    } catch {
      setAviso('O navegador não deixou copiar — selecione a chave e copie à mão.')
    }
  }

  if (carregando) {
    return <div className="h-[320px] animate-pulse rounded-sistema bg-superficie" />
  }
  if (!ficha) {
    return (
      <div role="alert" className="rounded-sistema bg-erro-suave px-4 py-3 text-sm text-erro-texto">
        {erro || 'Nota fiscal não encontrada.'}
      </div>
    )
  }

  const semDecisao = contarSemDecisao(ficha.itens, decisoes)
  const podeLancar = semDecisao === 0 && ficha.itens.length > 0 && ficha.itens_pendentes < ficha.itens.length
  const lancados = ficha.itens.filter((item) => item.conciliacao.status === 'lancado').length
  const totalDeItens = ficha.itens.length

  return (
    <div className="flex h-full flex-col">
      {/* ── cabeçalho: o documento se identifica antes de qualquer número ── */}
      <div className="flex items-start justify-between gap-3 pb-4">
        <div className="min-w-0">
          <p className="text-[12.5px] text-texto-suave">
            NF-e <span className="tabular">{ficha.numero}</span> · série{' '}
            <span className="tabular">{ficha.serie}</span>
          </p>
          <h2 className="mt-0.5 font-titulo text-[19px] leading-tight font-bold text-titulo">
            {ficha.fornecedor.nome}
          </h2>
          <p className="tabular mt-0.5 text-[13px] text-texto-suave">
            {ficha.fornecedor.documento
              ? mascararCNPJ(ficha.fornecedor.documento)
              : ficha.fornecedor.codigo}
          </p>
        </div>
        {aoFechar && (
          <button
            type="button"
            onClick={aoFechar}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-sistema bg-divisoria text-texto hover:bg-borda"
          >
            <Icone nome="close" tamanho={19} />
            <span className="sr-only">Fechar</span>
          </button>
        )}
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto border-t border-borda pt-4">
        {erro && (
          <div role="alert" className="rounded-sistema bg-erro-suave px-4 py-3 text-sm text-erro-texto">
            {erro}
          </div>
        )}
        {aviso && (
          <div role="status" className="rounded-sistema bg-marca-fundo-2 px-4 py-2.5 text-[13px] text-marca-forte">
            {aviso}
          </div>
        )}

        {/* ── situação: o que a nota é, e o que ainda falta nela ── */}
        <div className="rounded-sistema border border-borda bg-fundo px-4 py-3.5">
          <div className="flex flex-wrap gap-x-8 gap-y-3">
            <div>
              <p className={ROTULO}>Situação</p>
              <div className="mt-1">
                <SeloDaNota ficha={ficha} lancados={lancados} />
              </div>
              <p className="mt-1.5 text-[12.5px] text-texto-suave">
                {faltaNaNota(ficha, lancados, totalDeItens)}
              </p>
            </div>
            <div>
              <p className={ROTULO}>Emissão</p>
              <p className="tabular mt-1 text-[14px] font-semibold text-tinta">
                {new Date(ficha.emitida_em).toLocaleDateString('pt-BR')}
              </p>
            </div>
            <div>
              <p className={ROTULO}>Entrada</p>
              <p className="tabular mt-1 text-[14px] font-semibold text-tinta">
                {new Date(ficha.importada_em).toLocaleDateString('pt-BR')}
              </p>
            </div>
          </div>

          {ficha.natureza_operacao && (
            <div className="mt-3.5">
              <p className={ROTULO}>Natureza da operação</p>
              <p className="mt-1 text-[14px] font-semibold text-tinta">{ficha.natureza_operacao}</p>
            </div>
          )}
        </div>

        {/* ── chave de acesso: é o que se confere contra o DANFE ── */}
        <div>
          <div className="mb-1.5 flex items-center justify-between gap-2">
            <p className={ROTULO}>Chave de acesso</p>
            <button
              type="button"
              onClick={() => void copiarChave()}
              className="rounded-sistema px-2 py-0.5 text-[12.5px] font-semibold text-marca hover:bg-marca-fundo"
            >
              Copiar
            </button>
          </div>
          {/* Em blocos de 4: 44 dígitos corridos ninguém confere, e é assim que
              o DANFE imprime. */}
          <p className="tabular rounded-sistema bg-fundo px-3.5 py-2.5 text-[13px] break-all text-tinta">
            {ficha.chave_acesso.replace(/(\d{4})(?=\d)/g, '$1 ')}
          </p>
        </div>

        {/* ── itens ── */}
        <div>
          <p className={`${ROTULO} mb-1`}>
            {totalDeItens} {totalDeItens === 1 ? 'item' : 'itens'}
          </p>
          {ficha.itens.map((item) => (
            <LinhaDeItem
              key={item.numero_item}
              item={item}
              decisao={decisoes[item.numero_item]}
              conciliando={conciliandoItem === item.numero_item}
              aoConciliar={(codigo) => void conciliar(item, codigo)}
              aoPular={() => setDecisoes((atual) => ({ ...atual, [item.numero_item]: 'pular' }))}
              aoDesfazerPular={() => setDecisoes((atual) => semADecisao(atual, item.numero_item))}
            />
          ))}
        </div>

        {/* ── totais: soma, nunca campo solto ── */}
        <div className="rounded-sistema border border-borda bg-fundo px-4 py-3.5">
          <LinhaTotal rotulo="Produtos" valor={ficha.valor_produtos_centavos} />
          {ficha.valor_ibs_centavos > 0 && <LinhaTotal rotulo="IBS" valor={ficha.valor_ibs_centavos} />}
          {ficha.valor_cbs_centavos > 0 && <LinhaTotal rotulo="CBS" valor={ficha.valor_cbs_centavos} />}
          <div className="mt-2.5 flex items-baseline justify-between gap-3 border-t border-borda pt-2.5">
            <span className={ROTULO}>Total da nota</span>
            <span className="tabular font-titulo text-[22px] font-bold text-titulo">
              {formatarMoeda(ficha.valor_total_centavos)}
            </span>
          </div>
        </div>

        {resultadoLancamento && <ResumoDoLancamento resultado={resultadoLancamento} />}
      </div>

      {/* ── a decisão, no rodapé: é o que resta fazer com esta nota ── */}
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-borda pt-4">
        <p className="max-w-[46ch] text-[12.5px] text-texto-suave">
          {semDecisao > 0
            ? `${semDecisao} ${semDecisao === 1 ? 'item precisa' : 'itens precisam'} de uma decisão — conciliar ou pular.`
            : lancados === totalDeItens && totalDeItens > 0
              ? 'Tudo desta nota já entrou no estoque.'
              : ficha.itens_pendentes > 0
                ? // Pulado não some: ele fica pendente na nota, e quem lança
                  // precisa saber quanto está deixando para depois.
                  `${ficha.itens_pendentes} ${ficha.itens_pendentes === 1 ? 'item ficará pendente' : 'itens ficarão pendentes'} — você decidiu pular por enquanto.`
                : podeLancar
                  ? 'Pronta para lançar no estoque.'
                  : 'Sem item conciliado, não há o que lançar.'}
        </p>
        <div className="flex shrink-0 items-center gap-2.5">
          {aoFechar && (
            <Botao variante="secundario" onClick={aoFechar}>
                Fechar
              </Botao>
          )}
          <Botao
              icone="inventory_2"
              disabled={!podeLancar || lancando}
              onClick={() => void lancar()}
            >
              {lancando ? 'Lançando…' : 'Lançar no estoque'}
            </Botao>
        </div>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ peças */

/** O selo da nota: cor só no que ainda pede alguém. */
function SeloDaNota({ ficha, lancados }: { ficha: FichaNota; lancados: number }) {
  if (ficha.status === 'cancelada') return <Selo tom="riscado">Cancelada</Selo>
  if (ficha.itens.length > 0 && lancados >= ficha.itens.length) return <Selo tom="neutro">Lançada</Selo>
  if (lancados > 0) return <Selo tom="atencao">Parcial</Selo>
  return <Selo tom="info">Importada</Selo>
}

/** O que falta, em uma linha. É a pergunta que a gaveta responde. */
function faltaNaNota(ficha: FichaNota, lancados: number, total: number): string {
  if (ficha.status === 'cancelada') return 'fora das somas'
  if (ficha.itens_pendentes > 0) {
    return `falta conciliar ${ficha.itens_pendentes} ${ficha.itens_pendentes === 1 ? 'item' : 'itens'}`
  }
  if (total > 0 && lancados >= total) return 'estoque lançado'
  return 'falta lançar no estoque'
}

function LinhaTotal({ rotulo, valor }: { rotulo: string; valor: number }) {
  return (
    <div className="flex items-center justify-between gap-3 py-[3px] text-[13px]">
      <span className="text-texto-suave">{rotulo}</span>
      <span className="tabular text-tinta">{formatar(valor)}</span>
    </div>
  )
}

function ResumoDoLancamento({ resultado }: { resultado: ResultadoLancamento }) {
  return (
    <div className="flex flex-col gap-1.5 rounded-sistema bg-ok-suave px-4 py-3 text-[13px] text-ok-texto">
      <div className="flex items-center gap-2 font-semibold">
        <Icone nome="check_circle" tamanho={18} />
        Lançamento processado
      </div>
      <p>
        {resultado.itens_lancados.length}{' '}
        {resultado.itens_lancados.length === 1 ? 'item lançado' : 'itens lançados'} agora
        {resultado.itens_ja_lancados_antes.length > 0 &&
          ` · ${resultado.itens_ja_lancados_antes.length} já estava(m) lançado(s)`}
        {resultado.itens_pendentes.length > 0 &&
          ` · ${resultado.itens_pendentes.length} ainda pendente(s)`}
      </p>
    </div>
  )
}

/**
 * Um item é uma LINHA: descrição em cima, `2 × 39,90 · CP-001` embaixo, valor
 * à direita. O que o item exige — conciliar — só aparece quando exige.
 */
function LinhaDeItem({
  item,
  decisao,
  conciliando,
  aoConciliar,
  aoPular,
  aoDesfazerPular,
}: {
  item: ItemNota
  decisao?: 'pular'
  conciliando: boolean
  aoConciliar: (codigoBarras: string) => void
  aoPular: () => void
  aoDesfazerPular: () => void
}) {
  const [codigo, setCodigo] = useState('')
  const pendente = item.conciliacao.status === 'pendente'

  return (
    <div className="border-t border-divisoria py-3 first:border-t-0">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[13.5px] leading-snug font-semibold text-tinta">{item.descricao}</p>
          <p className="tabular mt-0.5 text-[12px] text-texto-suave">
            {item.quantidade} × {formatar(item.valor_unitario_centavos)} · {item.codigo_fornecedor}
            {item.ean ? ` · EAN ${item.ean}` : ''}
          </p>
        </div>
        <span className="tabular shrink-0 text-[13.5px] font-semibold text-tinta">
          {formatar(item.valor_total_centavos)}
        </span>
      </div>

      <div className="mt-1.5">
        <SeloDoItem item={item} decisao={decisao} />
      </div>

      {pendente && !decisao && (
        <form
          // Nomeado porque numa nota de trinta itens existem trinta botões
          // "Conciliar": sem o nome, nem leitor de tela nem teste conseguem
          // dizer de qual item é o formulário.
          aria-label={`Conciliar o item ${item.numero_item} — ${item.descricao}`}
          className="mt-2 flex items-center gap-1.5"
          onSubmit={(evento) => {
            evento.preventDefault()
            if (codigo.trim()) aoConciliar(codigo.trim())
          }}
        >
          {/* Aceita o bip E o nome. Quem recebe mercadoria raramente tem a peça
              etiquetada na mão — a etiqueta sai DEPOIS da entrada —, então
              exigir o código de barras era um beco sem saída para todo item
              cujo produto já está no catálogo. */}
          <BuscaDeSKU
            valor={codigo}
            aoDigitar={setCodigo}
            aoEscolher={(sku) => {
              setCodigo(sku.codigo_barras)
              aoConciliar(sku.codigo_barras)
            }}
            rotuloAcessivel={`Código de barras ou nome do SKU para o item ${item.numero_item}`}
            desabilitado={conciliando}
          />
          <button
            type="submit"
            disabled={conciliando || !codigo.trim()}
            className="h-8 shrink-0 rounded-sistema border border-borda bg-superficie px-2.5 text-[12px] font-semibold text-texto disabled:cursor-not-allowed disabled:text-texto-fraco"
          >
            {conciliando ? '…' : 'Conciliar'}
          </button>
          <button
            type="button"
            onClick={aoPular}
            className="h-8 shrink-0 rounded-sistema px-2 text-[12px] font-semibold text-texto-suave hover:text-texto"
          >
            Pular
          </button>
        </form>
      )}

      {decisao === 'pular' && (
        <button
          type="button"
          onClick={aoDesfazerPular}
          className="mt-1.5 text-[12px] font-semibold text-marca underline-offset-2 hover:underline"
        >
          Desfazer
        </button>
      )}
    </div>
  )
}

function SeloDoItem({ item, decisao }: { item: ItemNota; decisao?: 'pular' }): ReactNode {
  const sku = item.conciliacao.sku

  if (item.conciliacao.status === 'lancado') {
    return (
      <Selo tom="neutro">
        {sku ? `${sku.produto} · ${sku.cor} ${sku.tamanho}` : item.conciliacao.status_rotulo}
      </Selo>
    )
  }
  if (item.conciliacao.status === 'conciliado') {
    return (
      <span className="inline-flex flex-wrap items-center gap-1.5">
        <Selo tom="info">
          {sku ? `${sku.produto} · ${sku.cor} ${sku.tamanho}` : item.conciliacao.status_rotulo}
        </Selo>
        {sku && <span className="tabular text-[11px] text-texto-fraco">{sku.codigo_barras}</span>}
      </span>
    )
  }
  if (decisao === 'pular') {
    return <Selo tom="neutro">Pulado por enquanto</Selo>
  }
  return (
    <Selo tom="erro">
      Sem SKU
    </Selo>
  )
}
